/**
 * Forgot password page
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useForgotPassword } from '../../hooks/useAuth';
import { useSEO, SEO_CONFIGS } from '../../hooks/useSEO';

const forgotPasswordSchema = z.object({
  email: z.string().email('Email inválido'),
});

type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPasswordPage() {
  // SEO meta tags for forgot password page
  useSEO(SEO_CONFIGS.forgotPassword);

  const [submitted, setSubmitted] = useState(false);
  const forgotPassword = useForgotPassword();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordForm>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = (data: ForgotPasswordForm) => {
    forgotPassword.mutate(data.email, {
      onSuccess: () => setSubmitted(true),
    });
  };

  if (submitted) {
    return (
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-green-100 text-green-600 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-8 h-8" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Email Enviado!</h1>
        <p className="text-gray-600 mb-8">
          Se o email existir em nossa base, você receberá as instruções para redefinir sua senha.
        </p>
        <Link to="/entrar">
          <Button variant="outline" className="w-full">
            <ArrowLeft className="w-4 h-4" />
            Voltar para o login
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Recuperar Senha</h1>
        <p className="text-gray-600 mt-2">
          Digite seu email para receber as instruções de recuperação
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="relative">
          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
          <Input
            {...register('email')}
            type="email"
            placeholder="Seu email"
            className="pl-12"
            error={errors.email?.message}
            autoComplete="email"
          />
        </div>

        <Button
          type="submit"
          className="w-full"
          loading={forgotPassword.isPending}
        >
          Enviar Instruções
        </Button>
      </form>

      <p className="mt-8 text-center">
        <Link to="/entrar" className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Voltar para o login
        </Link>
      </p>
    </div>
  );
}
